import {
  Box,
  FormControl,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  FormHelperText,
  InputAdornment,
  IconButton,
  Typography,
  Alert,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { addNewAdmin } from "../UserManagement/UserManagementActions";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import RegisterAdminStyles from "./RegisterAdminStyles";
import { adminGetPasswordConfig } from "../PasswordConfiguration/passwordConfigurationActions";
import { RootState } from "../../store/store";
import { IPasswordConfigData } from "../PasswordConfiguration/passwordConfigurationType";
import {
  createPasswordValidationRegex,
  getPasswordValidationMessage,
} from "../../utils/Utility";

const RegisterAdmin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isSuccess, calledReducerType, isError, message } = useAppSelector(
    (state) => state.common
  );
  const { passwordConfigData } = useAppSelector(
    (state: RootState) => state.adminPasswordConfig
  );

  // State for form fields
  const [formData, setFormData] = useState({
    token: "",
    displayName: "",
    password: "",
    confirmPassword: "",
  });

  // State for error messages
  const [errors, setErrors] = useState({
    token: "",
    displayName: "",
    password: "",
    confirmPassword: "",
  });

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  // Create regex pattern when passwordConfigData changes
  const [passwordRegex, setPasswordRegex] = useState<RegExp | null>(null);

  // Update regex when passwordConfigData changes
  useEffect(() => {
    if (passwordConfigData) {
      setPasswordRegex(createPasswordValidationRegex(passwordConfigData));
    }
  }, [passwordConfigData]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validate password
    if (name === "password" && passwordConfigData && passwordRegex) {
      if (value && !passwordRegex.test(value)) {
        const passwordError = getPasswordValidationMessage(
          value,
          passwordConfigData
        );
        setErrors((prev) => ({ ...prev, password: passwordError }));
      } else {
        setErrors((prev) => ({ ...prev, password: "" }));
      }
    }

    // Validate confirm password
    if (name === "confirmPassword") {
      const confirmError =
        value !== formData.password ? "Passwords do not match" : "";
      setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }

    // Clear other field errors
    if (name !== "password" && name !== "confirmPassword") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let newErrors = {
      token: "",
      displayName: "",
      password: "",
      confirmPassword: "",
    };

    // Validate fields
    if (!formData.token) newErrors.token = "Token is required";
    if (!formData.displayName)
      newErrors.displayName = "Display Name is required";
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (passwordConfigData) {
      const passwordError = getPasswordValidationMessage(
        formData.password,
        passwordConfigData
      );
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirm Password is required";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Check if there are any errors
    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
      return;
    }

    if (formData) {
      dispatch(addNewAdmin(formData));
    }
  };
  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    if (isSuccess && calledReducerType === "adminUsers/addNewAdmin") {
      navigate("/admin/login");
    }
  };

  useEffect(() => {
    dispatch(adminGetPasswordConfig());
  }, [dispatch]);

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "adminUsers/addNewAdmin"
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType, addNewAdmin]);

  return (
    <Dialog open={true} onClose={() => {}} sx={{ backdropFilter: "blur(5px)" }}>
      <DialogTitle>Register Admin</DialogTitle>
      <DialogContent>
        <Box sx={RegisterAdminStyles.dialogBoxContainer}>
          <MessageDialog
            open={messageDialogOpen}
            handleClose={handleMessageDialogClose}
          />
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.token}
                >
                  <TextField
                    name="token"
                    label="Token"
                    value={formData.token}
                    onChange={handleChange}
                    variant="outlined"
                    required
                  />
                  <FormHelperText>{errors.token}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.displayName}
                >
                  <TextField
                    name="displayName"
                    label="Display Name"
                    value={formData.displayName}
                    onChange={handleChange}
                    variant="outlined"
                    required
                  />
                  <FormHelperText>{errors.displayName}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.password}
                >
                  <TextField
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormHelperText>{errors.password}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.confirmPassword}
                >
                  <TextField
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormHelperText>{errors.confirmPassword}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ borderRadius: "4px" }}
                >
                  Register
                </Button>
              </Grid>
            </Grid>
          </form>
          <Typography variant="body2" sx={{ marginTop: 2 }}>
            Already have an account?{" "}
            <Link
              to="/admin/login"
              style={{ textDecoration: "none", color: "#1976d2" }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterAdmin;
